import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { CODES_PARAM, type PortalConfig } from './config';
import type { AttendancePunch, ConnectionState } from './types';

/** Server-to-client method name. Must match `AttendancePortalHub.PunchMethod`. */
const ATTENDANCE_PUNCH_METHOD = 'AttendancePunch';

/**
 * Reconnect backoff, in milliseconds.
 *
 * Deliberately unbounded in count (SignalR repeats the last value): a kiosk must recover on its own
 * after an overnight outage, with nobody there to press anything. Early attempts are fast so a brief
 * blip is invisible; it then settles to every 30s rather than hammering a server that may itself be
 * restarting.
 */
const RECONNECT_DELAYS_MS = [0, 2_000, 5_000, 10_000, 30_000];

/** How long to wait before rebuilding a connection SignalR has given up on entirely. */
const COLD_RETRY_MS = 30_000;

export class AttendanceHub {
  private connection: HubConnection | null = null;

  constructor(
    private readonly config: PortalConfig,
    private readonly onPunch: (punch: AttendancePunch) => void,
    private readonly onStateChange: (state: ConnectionState) => void,
  ) {}

  async start(): Promise<void> {
    // Codes travel on the connection URL, so the server can put this connection into the right
    // terminal groups in OnConnectedAsync — before any punch can arrive and be missed.
    const url = `${this.config.attendanceHubUrl}?${CODES_PARAM}=${encodeURIComponent(this.config.codes.join(','))}`;

    this.connection = new HubConnectionBuilder()
      .withUrl(url)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (context) =>
          RECONNECT_DELAYS_MS[Math.min(context.previousRetryCount, RECONNECT_DELAYS_MS.length - 1)],
      })
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.on(ATTENDANCE_PUNCH_METHOD, (punch: AttendancePunch) => this.onPunch(punch));

    this.connection.onreconnecting(() => this.onStateChange('reconnecting'));
    this.connection.onreconnected(() => this.onStateChange('connected'));

    // withAutomaticReconnect eventually gives up; this is the kiosk's own last resort.
    this.connection.onclose(() => {
      this.onStateChange('disconnected');
      void this.retryFromScratch();
    });

    this.onStateChange('connecting');
    await this.connectWithRetry();
  }

  private async connectWithRetry(): Promise<void> {
    try {
      await this.connection!.start();
      this.onStateChange('connected');
    } catch {
      this.onStateChange('disconnected');
      await this.retryFromScratch();
    }
  }

  private async retryFromScratch(): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected) {
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, COLD_RETRY_MS));
    await this.connectWithRetry();
  }
}
